/**
 * Formatierungshilfen.
 *
 * Die Schreibweise folgt der gewaehlten Oberflaechensprache: Deutsch nutzt
 * Komma als Dezimaltrennzeichen, Englisch den Punkt. Die Sprache wird vom
 * I18nProvider gesetzt, damit die Formatierer ohne Hook auskommen.
 */

type FormatLocale = 'de-DE' | 'en-GB';

let activeLocale: FormatLocale = 'de-DE';

/** Wird vom I18nProvider bei jedem Sprachwechsel aufgerufen. */
export function setFormatLocale(locale: 'de' | 'en'): void {
  activeLocale = locale === 'en' ? 'en-GB' : 'de-DE';
}

export function currentFormatLocale(): FormatLocale {
  return activeLocale;
}

/** Dezimaltrennzeichen der aktiven Sprache. */
function decimalSeparator(): string {
  return activeLocale === 'de-DE' ? ',' : '.';
}

/** Datenmenge mit binaeren Praefixen: 1536 -> "1,5 KiB". */
export function formatBytes(bytes: number, digits = 1): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'];
  const exponent = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / 1024 ** exponent;
  return `${value.toFixed(exponent === 0 ? 0 : digits).replace('.', decimalSeparator())} ${units[exponent]}`;
}

/** Datenrate mit dezimalen Praefixen: 1_500_000 -> "1,5 Mbit/s". */
export function formatBitrate(bitsPerSecond: number, digits = 1): string {
  if (!Number.isFinite(bitsPerSecond) || bitsPerSecond <= 0) return '0 bit/s';
  const units = ['bit/s', 'kbit/s', 'Mbit/s', 'Gbit/s', 'Tbit/s'];
  const exponent = Math.min(units.length - 1, Math.floor(Math.log10(bitsPerSecond) / 3));
  const value = bitsPerSecond / 1000 ** exponent;
  return `${value.toFixed(exponent === 0 ? 0 : digits).replace('.', decimalSeparator())} ${units[exponent]}`;
}

/** Tarif-Bandbreite: 1000 -> "1 Gbit/s", 240 -> "240 Mbit/s". */
export function formatPlanBandwidth(mbps: number): string {
  if (mbps >= 1000) {
    const gbit = mbps / 1000;
    return `${(Number.isInteger(gbit) ? gbit : gbit.toFixed(1)).toString().replace('.', decimalSeparator())} Gbit/s`;
  }
  return `${mbps} Mbit/s`;
}

export function formatPrice(cents: number, currency = 'EUR'): string {
  return new Intl.NumberFormat(activeLocale, { style: 'currency', currency }).format(cents / 100);
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat(activeLocale).format(value);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '-';
  return new Intl.DateTimeFormat(activeLocale, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

/** Relative Zeitangabe: "vor 12 Sekunden", "vor 3 Minuten". */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return activeLocale === 'de-DE' ? 'nie' : 'never';
  const deltaSeconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (deltaSeconds < 5) return activeLocale === 'de-DE' ? 'gerade eben' : 'just now';

  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 30],
    ['month', 12],
    ['year', Number.POSITIVE_INFINITY],
  ];

  const relative = new Intl.RelativeTimeFormat(activeLocale, { numeric: 'auto' });
  let value = deltaSeconds;
  for (const [unit, size] of units) {
    if (Math.abs(value) < size) return relative.format(-Math.round(value), unit);
    value /= size;
  }
  return formatDateTime(iso);
}

/** Uhrzeit fuer Diagramm-Achsen. */
export function formatClock(iso: string): string {
  return new Intl.DateTimeFormat(activeLocale, { hour: '2-digit', minute: '2-digit' }).format(
    new Date(iso),
  );
}

/** Kuerzt lange Schluessel fuer die Anzeige. */
export function truncateKey(key: string, visible = 10): string {
  return key.length <= visible * 2 ? key : `${key.slice(0, visible)}...${key.slice(-4)}`;
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Loest einen Datei-Download aus einem String aus. */
export function downloadText(filename: string, content: string, mimeType = 'text/plain'): void {
  const url = URL.createObjectURL(new Blob([content], { type: `${mimeType};charset=utf-8` }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
